import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Save,
  Trash2,
  Download,
  Upload,
  Star,
  MoreVertical,
  Plus,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { FilterPreset } from '@/types/utilisation';
import {
  loadPresets,
  savePreset,
  deletePreset,
  updatePreset,
  exportPresets,
  importPresets,
  generatePresetId,
} from '@/utils/filterPresetUtils';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface FilterPresetManagerProps {
  currentFilters: FilterPreset['filters'];
  activePresetId: string | null;
  onLoadPreset: (preset: FilterPreset) => void;
  onClearPreset: () => void;
  className?: string;
}

export const FilterPresetManager: React.FC<FilterPresetManagerProps> = ({
  currentFilters,
  activePresetId,
  onLoadPreset,
  onClearPreset,
  className,
}) => {
  const [presets, setPresets] = useState<FilterPreset[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [editingPreset, setEditingPreset] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Load presets on mount
  useEffect(() => {
    setPresets(loadPresets());
  }, []);

  // Refresh presets
  const refreshPresets = () => {
    setPresets(loadPresets());
  };

  // Handle save preset
  const handleSavePreset = () => {
    if (!presetName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    const newPreset: FilterPreset = {
      id: generatePresetId(),
      name: presetName.trim(),
      filters: currentFilters,
      createdAt: new Date(),
      isDefault: false,
    };

    const result = savePreset(newPreset);
    if (result.success) {
      toast.success('Preset saved successfully');
      refreshPresets();
      setSaveDialogOpen(false);
      setPresetName('');
    } else {
      toast.error(result.error || 'Failed to save preset');
    }
  };

  // Handle load preset
  const handleLoadPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      onLoadPreset(preset);
      toast.success(`Loaded preset: ${preset.name}`);
    }
  };

  // Handle delete preset
  const handleDeletePreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;

    const result = deletePreset(presetId);
    if (result.success) {
      toast.success(`Deleted preset: ${preset.name}`);
      refreshPresets();
      if (activePresetId === presetId) {
        onClearPreset();
      }
    } else {
      toast.error(result.error || 'Failed to delete preset');
    }
  };

  // Handle rename preset
  const handleRenamePreset = (presetId: string) => {
    if (!editName.trim()) {
      toast.error('Please enter a preset name');
      return;
    }

    const result = updatePreset(presetId, { name: editName.trim() });
    if (result.success) {
      toast.success('Preset renamed successfully');
      refreshPresets();
      setEditingPreset(null);
      setEditName('');
    } else {
      toast.error(result.error || 'Failed to rename preset');
    }
  };

  // Handle export presets
  const handleExportPresets = () => {
    try {
      const json = exportPresets();
      const blob = new Blob([json], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `utilisation-presets-${format(new Date(), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Presets exported successfully');
    } catch (error) {
      toast.error('Failed to export presets');
    }
  };

  // Handle import presets
  const handleImportPresets = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = event.target?.result as string;
          const result = importPresets(data);
          if (result.success) {
            toast.success(`Imported ${result.imported} preset(s)`);
            refreshPresets();
          } else {
            toast.error(result.error || 'Failed to import presets');
          }
        } catch (error) {
          toast.error('Failed to read preset file');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const userPresets = presets.filter((p) => !p.isDefault);
  const defaultPresets = presets.filter((p) => p.isDefault);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Preset Selector */}
      <Select
        value={activePresetId || 'none'}
        onValueChange={(value) => {
          if (value === 'none') {
            onClearPreset();
          } else {
            handleLoadPreset(value);
          }
        }}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Load preset..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            <span className="text-muted-foreground">No preset</span>
          </SelectItem>
          
          {defaultPresets.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                Default Presets
              </div>
              {defaultPresets.map((preset) => (
                <SelectItem key={preset.id} value={preset.id}>
                  <div className="flex items-center gap-2">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    {preset.name}
                  </div>
                </SelectItem>
              ))}
            </>
          )}

          {userPresets.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                My Presets
              </div>
              {userPresets.map((preset) => (
                <SelectItem key={preset.id} value={preset.id}>
                  {preset.name}
                </SelectItem>
              ))}
            </>
          )}

          {userPresets.length === 0 && defaultPresets.length === 0 && (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              No presets available
            </div>
          )}
        </SelectContent>
      </Select>

      {/* Active Preset Indicator */}
      {activePresetId && (
        <Badge variant="secondary" className="gap-1">
          <Check className="h-3 w-3" />
          {presets.find((p) => p.id === activePresetId)?.name}
        </Badge>
      )}

      {/* Actions Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Preset actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[200px]">
          <DropdownMenuItem onClick={() => setSaveDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Save Current Filters
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setManageDialogOpen(true)}>
            <Save className="mr-2 h-4 w-4" />
            Manage Presets
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleExportPresets}>
            <Download className="mr-2 h-4 w-4" />
            Export Presets
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleImportPresets}>
            <Upload className="mr-2 h-4 w-4" />
            Import Presets
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save Preset Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>
              Save your current filter settings as a preset for quick access later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name">Preset Name</Label>
              <Input
                id="preset-name"
                placeholder="e.g., Q4 Engineering Team"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSavePreset();
                  }
                }}
              />
            </div>
            <div className="rounded-lg border p-3 space-y-2 text-sm">
              <p className="font-medium">Current Filters:</p>
              <div className="space-y-1 text-muted-foreground">
                <p>
                  • Date:{' '}
                  {currentFilters.dateMode === 'custom'
                    ? 'Custom range'
                    : currentFilters.selectedRange || 'Not set'}
                </p>
                <p>
                  • Department:{' '}
                  {currentFilters.selectedDepartment === 'all'
                    ? 'All'
                    : currentFilters.selectedDepartment}
                </p>
                <p>
                  • Employees:{' '}
                  {currentFilters.selectedEmployees.length === 0
                    ? 'All'
                    : `${currentFilters.selectedEmployees.length} selected`}
                </p>
                <p>• Group By: {currentFilters.selectedGroupBy}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePreset}>Save Preset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Presets Dialog */}
      <Dialog open={manageDialogOpen} onOpenChange={setManageDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Filter Presets</DialogTitle>
            <DialogDescription>
              View, rename, or delete your saved filter presets.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {/* Default Presets */}
              {defaultPresets.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    Default Presets
                  </h3>
                  <div className="space-y-2">
                    {defaultPresets.map((preset) => (
                      <div
                        key={preset.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-muted/50"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{preset.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Default preset (cannot be modified)
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleLoadPreset(preset.id);
                            setManageDialogOpen(false);
                          }}
                        >
                          Load
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User Presets */}
              {userPresets.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">My Presets</h3>
                  <div className="space-y-2">
                    {userPresets.map((preset) => (
                      <div
                        key={preset.id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex-1">
                          {editingPreset === preset.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleRenamePreset(preset.id);
                                  } else if (e.key === 'Escape') {
                                    setEditingPreset(null);
                                    setEditName('');
                                  }
                                }}
                                className="h-8"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                onClick={() => handleRenamePreset(preset.id)}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingPreset(null);
                                  setEditName('');
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <>
                              <p className="font-medium">{preset.name}</p>
                              <p className="text-xs text-muted-foreground">
                                Created {format(preset.createdAt, 'MMM d, yyyy')}
                              </p>
                            </>
                          )}
                        </div>
                        {editingPreset !== preset.id && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                handleLoadPreset(preset.id);
                                setManageDialogOpen(false);
                              }}
                            >
                              Load
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingPreset(preset.id);
                                setEditName(preset.name);
                              }}
                            >
                              Rename
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeletePreset(preset.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {userPresets.length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  <Save className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">No custom presets yet</p>
                  <p className="text-xs mt-1">
                    Save your current filters to create a preset
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
